// src/components/users/forms/CreateUserForm.tsx
import React, { useMemo, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import Select from "@/components/shared/ui/Select";
import LocationRow, {
  type LocationFormValue as CSCFormValue,
} from "@/components/shared/forms/LocationRow";

import {
  FiMail,
  FiPhone,
  FiShield,
  FiUser,
  FiCalendar,
  FiHash,
  FiHome,
  FiMapPin,
} from "react-icons/fi";

import { useRoles } from "@/api/features/roles/queries";
import type { Role as RoleModel } from "@/api/features/roles/types";

// 🔹 Import canonical types from the Users feature
import type {
  Gender,
  IdType,
  CreateUserBody,
} from "@/api/features/users/types";

/* =========================
 * Types
 * ========================= */

/**
 * Form-level type for creating a user.
 *
 * - Based on backend CreateUserBody to stay in sync.
 * - Only difference: roleId can be null in the form (until user picks it).
 * - We omit otpChannel / userCode here; they can be added later if needed.
 */
export type UpsertUser = Omit<
  CreateUserBody,
  "roleId" | "otpChannel" | "userCode"
> & {
  roleId: number | null;
};

export default function CreateUserForm({
  onSubmit,
  submitting = false,
  onCancel,
  errorMessage,
}: {
  onSubmit: (payload: UpsertUser) => void;
  submitting?: boolean;
  onCancel?: () => void;
  /** Server-side error to show on top (e.g. from API validation) */
  errorMessage?: string | null;
}) {
  /* =========================
   * Local form state
   * ========================= */
  const [form, setForm] = useState<UpsertUser>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phone: "",
    roleId: null, // dynamic, required before submit

    // Profile
    gender: "Male",
    dob: "",
    idType: "National ID",
    idNumber: "",

    // Address
    addressLine1: "",
    addressLine2: "",
    country: "",
    state: "",
    city: "",

    // Auth
    password: "",
  });

  // Local client-side validation error
  const [localError, setLocalError] = useState<string | null>(null);

  const change = <K extends keyof UpsertUser>(k: K, v: UpsertUser[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (localError) setLocalError(null);
  };

  /* =========================
   * Dynamic Roles dropdown
   * ========================= */
  const { data, isLoading, isError, refetch } = useRoles({
    page: 1,
    pageSize: 100,
  });

  const roles: RoleModel[] = data?.items ?? [];

  const roleNameToId = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of roles) map.set(r.name, r.id);
    return map;
  }, [roles]);

  const roleIdToName = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of roles) map.set(r.id, r.name);
    return map;
  }, [roles]);

  const roleLabel =
    form.roleId != null ? roleIdToName.get(form.roleId) ?? "" : "";

  const roleOptions: string[] = useMemo(() => {
    if (isLoading) return ["Loading roles…"];
    if (isError) return ["Failed to load roles"];
    if (!roles.length) return ["No active roles"];
    // Only "system user" roles are assignable here. Section-managed roles
    // (Customer, Affiliate Partner, Supplier, …) are flagged isSystemRole=false
    // and created from their own dedicated sections instead.
    return roles.filter((r) => r.isSystemRole).map((r) => r.name);
  }, [isLoading, isError, roles]);

  const roleSelectDisabled = isLoading || isError || roles.length === 0;

  /* =========================
   * Submit / Cancel
   * ========================= */
  const submit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic client-side validation for required fields
    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.username.trim() ||
      !form.email.trim()
    ) {
      setLocalError(
        "Please fill in first name, last name, username, and email."
      );
      return;
    }

    const trimmedPassword = form.password.trim();
    if (!trimmedPassword) {
      setLocalError("Please set a password.");
      return;
    }
    if (trimmedPassword.length < 8) {
      setLocalError("Password must be at least 8 characters.");
      return;
    }

    if (form.roleId == null) {
      setLocalError("Please select a role.");
      return;
    }

    setLocalError(null);

    // Normalize dob: empty string -> empty string (page hook can adapt further)
    const cleanedForm: UpsertUser = {
      ...form,
      dob: form.dob && form.dob.trim() ? form.dob : "",
    };

    onSubmit(cleanedForm);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      history.back();
    }
  };

  const bannerMessage = localError || errorMessage || null;

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {/* Top error banner */}
      {bannerMessage && (
        <div
          className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
          aria-live="polite"
        >
          {bannerMessage}
        </div>
      )}

      {/* Name */}
      <Field label="First name" icon={<FiUser />}>
        <Input
          value={form.firstName}
          onChange={(e) => change("firstName", e.target.value)}
          placeholder="Enter first name"
          required
        />
      </Field>
      <Field label="Last name" icon={<FiUser />}>
        <Input
          value={form.lastName}
          onChange={(e) => change("lastName", e.target.value)}
          placeholder="Enter last name"
          required
        />
      </Field>

      {/* Username */}
      <Field label="Username" icon={<FiUser />}>
        <Input
          value={form.username}
          onChange={(e) => change("username", e.target.value)}
          placeholder="Unique username"
          required
        />
      </Field>

      {/* Contact */}
      <Field label="Email" icon={<FiMail />}>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => change("email", e.target.value)}
          placeholder="name@company.com"
          required
        />
      </Field>
      <Field label="Phone" icon={<FiPhone />}>
        <PhoneInput
          country="in"
          value={form.phone}
          onChange={(v) => change("phone", `+${v}`)}
          inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
          buttonClass="!border-gray-300"
        />
      </Field>

      {/* Password */}
      <Field label="Password" icon={<FiShield />}>
        <Input
          type="password"
          value={form.password}
          onChange={(e) => change("password", e.target.value)}
          placeholder="At least 8 characters"
          required
        />
        <p className="mt-1 text-xs text-gray-500">
          Must be at least 8 characters.
        </p>
      </Field>

      {/* Profile */}
      <Field label="Gender" icon={<FiUser />}>
        <Select
          value={form.gender || ""}
          onChange={(e) => change("gender", e.target.value as Gender)}
          options={["Male", "Female", "Other"]}
        />
      </Field>
      <Field label="Date of birth" icon={<FiCalendar />}>
        <Input
          type="date"
          value={form.dob ?? ""}
          onChange={(e) => change("dob", e.target.value)}
          placeholder="YYYY-MM-DD"
        />
      </Field>

      {/* Identity */}
      <Field label="Identification type" icon={<FiHash />}>
        <Select
          value={form.idType || ""}
          onChange={(e) => change("idType", e.target.value as IdType)}
          options={["National ID", "Passport"]}
        />
      </Field>
      <Field label="ID number" icon={<FiHash />}>
        <Input
          value={form.idNumber ?? ""}
          onChange={(e) => change("idNumber", e.target.value)}
          placeholder="Enter ID / Passport no."
        />
      </Field>

      {/* Dynamic Role */}
      <Field label="Role" icon={<FiShield />}>
        <div className="flex items-center gap-2">
          <Select
            value={roleLabel || ""}
            onChange={(e) => {
              const label = e.target.value;
              const id = roleNameToId.get(label) ?? null;
              change("roleId", id);
            }}
            options={roleOptions}
            placeholder={
              isLoading
                ? "Loading…"
                : roles.length
                ? "Select role"
                : "No roles"
            }
            disabled={roleSelectDisabled}
          />
          {isError && (
            <button
              type="button"
              onClick={() => refetch()}
              className="text-xs text-sky-600 hover:underline"
            >
              Retry
            </button>
          )}
        </div>
      </Field>

      {/* Address section */}
      <div className="mt-4 sm:col-span-2 text-sm font-semibold text-gray-800">
        Address &amp; Contact
      </div>
      <Field label="Address line 1" icon={<FiHome />}>
        <Input
          value={form.addressLine1 ?? ""}
          onChange={(e) => change("addressLine1", e.target.value)}
          placeholder="House / Flat / Street"
        />
      </Field>
      <Field label="Address line 2" icon={<FiMapPin />}>
        <Input
          value={form.addressLine2 ?? ""}
          onChange={(e) => change("addressLine2", e.target.value)}
          placeholder="Area / Landmark (optional)"
        />
      </Field>

      {/* Country → State → City */}
      <div className="sm:col-span-2">
        <LocationRow
          value={{
            country: form.country,
            state: form.state,
            city: form.city,
          }}
          onChange={(v: CSCFormValue) => {
            setForm((p) => ({
              ...p,
              country: v.country || "",
              state: v.state || "",
              city: v.city || "",
            }));
            if (localError) setLocalError(null);
          }}
          instancePrefix="user-csc"
        />
      </div>

      {/* Actions */}
      <div className="mt-2 flex justify-end gap-2 sm:col-span-2">
        <button
          type="button"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
          disabled={submitting || form.roleId == null || roleSelectDisabled}
        >
          {submitting ? "Creating…" : "Create User"}
        </button>
      </div>
    </form>
  );
}
